import {
  ChartIcon,
  CheckIcon,
  Divider,
  Edit1Icon,
  FormControlLabel,
  GKButton,
  Grid2,
  Show,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  Typography,
} from "components";
import { FeePlansForm } from "interfaces/Revenue";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetFeePlanByIdQuery,
  useUpdateFeePlanMutation,
} from "store/features/Revenue/FeePlan";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { moneyCommaSeparator } from "utils";
import useDocumentTitle from "utils/useDocumentTitle";
import FeePlanDetailsModal from "./FeePlanDetailsModal";
import MapPortfolioModal from "./MapPortfolioModal";
import PartnerHistory from "./PartnerHistory";
import PortfoliosTab from "./PortfoliosTab";
import RevenueProducts from "./RevenueProducts";
import { scrollToFirstError } from "utils/helpers";

const FeePlanEditForm = () => {
  useDocumentTitle("Fee Plan Details");
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: getFeePlanById, isFetching: getFeePlanFetching } =
    useGetFeePlanByIdQuery(id as string, { skip: !id });

  const [updateFeePlan, { isLoading }] = useUpdateFeePlanMutation();

  const dispatch = useAppDispatch();
  const { editFeePlanTab } = useAppSelector((state) => state?.app?.tab);

  const [openEditFeePlanModal, setOpenEditFeePlanModal] = useState(false);
  const [mapPortfolioModal, setMapPortfolioModal] = useState(false);

  const form = useForm<FeePlansForm>({
    defaultValues: {
      name: "",
      revenueProducts: [],
      description: "",
      status: false,
      managementFee: {
        name: "",
        hsnSacCode: "",
        fee: "",
        feeType: "",
        calculationMethod: "",
        advanceType: "",
        feeParameters: "",
        billingFrequency: "",
        outOfPocketExpense: null,
        minimumFee: null,
        maximumFee: null,
        feeMethod: "Simple",
      },
      performanceFee: {
        name: "",
        hsnSacCode: "",
        fee: "",
        feeParameters: "",
        billingFrequency: "",
        outOfPocketExpense: null,
        highWatermark: false,
        minesManagementFee: false,
        includeDividend: false,
        hurdle: false,
        hurdleType: "",
        hurdleRate: "",
        minimumFee: null,
        maximumFee: null,
      },
      subscriptionFee: {
        name: "",
        hsnSacCode: "",
        isFree: false,
        fee: "0",
        feeType: "",
        feeParameters: "",
        minimumInvestment: null,
        availableFrequency: "",
        subscriptionPlan: [
          {
            frequency: "",
            planFee: "",
            discountAmount: "",
            discountType: "Amount",
            taxableAmount: "",
            gstAmount: "",
            totalAmount: "",
          },
        ],
      },
    },
    values: getFeePlanById?.data,
  });

  return (
    <DashboardLayout title="Edit Fee Plan Details" loading={getFeePlanFetching}>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(
            async (values) => {
              try {
                await updateFeePlan({ id, body: values }).unwrap();
                navigate(-1);
              } catch (err: any) {
                console.error("Create fee plan failed:", err);
              }
            },
            (errors) => scrollToFirstError(errors, form),
          )}
        >
          <Grid2 container spacing={1} display={"flex"} alignItems={"center"}>
            <Grid2 size={7}>
              <Typography
                fontSize={14}
                fontWeight={700}
                color="var(--neutral-900)"
              >
                {form.getValues("name") || "-"}
              </Typography>
            </Grid2>
            <Grid2
              size={5}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-end"}
              gap={1}
            >
              <Controller
                name="status"
                control={form.control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ gap: 1 }}
                    control={<Switch {...field} checked={field.value} />}
                    label={
                      <Typography
                        fontSize={13}
                        fontWeight={600}
                        color="var(--grey-900)"
                      >
                        Set Plan as Active
                      </Typography>
                    }
                    labelPlacement="start"
                  />
                )}
              />
              <Divider orientation="vertical" sx={{ height: "40px" }} />
              <GKButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={() => setOpenEditFeePlanModal(true)}
                startIcon={<Edit1Icon />}
              >
                Edit Details
              </GKButton>
              <GKButton
                variant="contained"
                startIcon={<ChartIcon />}
                onClick={() => setMapPortfolioModal(true)}
              >
                Map Portfolio
              </GKButton>{" "}
            </Grid2>

            <Show.When isTrue={!!form.getValues("description")}>
              <Grid2 size={7} maxHeight={"150px"} overflow={"auto"}>
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  color="var(--grey-700)"
                  dangerouslySetInnerHTML={{
                    __html: form.getValues("description"),
                  }}
                />
              </Grid2>
            </Show.When>

            <Grid2 size={12} mt={2}>
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell style={{ padding: 16, borderBottom: "none" }}>
                        <Typography
                          fontSize={12}
                          fontWeight={400}
                          color={"var(--neutral-700)"}
                        >
                          Total Portfolios
                        </Typography>
                        <Typography
                          fontSize={16}
                          fontWeight={600}
                          color={"var(--black)"}
                        >
                          {form.getValues("totalPortfolio")}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ padding: 16, borderBottom: "none" }}>
                        <Typography
                          fontSize={12}
                          fontWeight={400}
                          color={"var(--neutral-700)"}
                        >
                          Total AUM Mapped
                        </Typography>
                        <Typography
                          fontSize={16}
                          fontWeight={600}
                          color={"var(--black)"}
                        >
                          {moneyCommaSeparator(
                            +form.getValues("totalAumMapped"),
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ padding: 16, borderBottom: "none" }}>
                        <Typography
                          fontSize={12}
                          fontWeight={400}
                          color={"var(--neutral-700)"}
                        >
                          Total Fee Collected
                        </Typography>
                        <Typography
                          fontSize={16}
                          fontWeight={600}
                          color={"var(--black)"}
                        >
                          {moneyCommaSeparator(
                            +form.getValues("totalFeeCollected"),
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ padding: 16, borderBottom: "none" }}>
                        <Typography
                          fontSize={12}
                          fontWeight={400}
                          color={"var(--neutral-700)"}
                        >
                          Total Fee Generated
                        </Typography>
                        <Typography
                          fontSize={16}
                          fontWeight={600}
                          color={"var(--black)"}
                        >
                          {moneyCommaSeparator(
                            +form.getValues("totalFeeGenerated"),
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid2>

            <Grid2 size={12} mt={2}>
              <Divider />
            </Grid2>
          </Grid2>

          <Grid2 container spacing={2} mt={1}>
            <Grid2 size={12}>
              <Tabs
                value={editFeePlanTab}
                onChange={(_, val) => {
                  dispatch(TAB({ tabKey: "editFeePlanTab", tabValue: val }));
                }}
              >
                <Tab disableRipple label="Revenue Products" />
                <Tab disableRipple label="Portfolios" />
                <Tab disableRipple label="Partners" />
              </Tabs>
              <Divider />
            </Grid2>
            <Grid2 size={12}>
              {editFeePlanTab === 0 && (
                <RevenueProducts data={getFeePlanById?.data} />
              )}
              {editFeePlanTab === 1 && <PortfoliosTab />}
              {editFeePlanTab === 2 && <PartnerHistory />}
            </Grid2>

            {editFeePlanTab === 0 && (
              <Grid2 size={12} display={"flex"} justifyContent={"flex-end"}>
                <GKButton
                  variant="contained"
                  type="submit"
                  startIcon={<CheckIcon />}
                  loading={isLoading}
                >
                  Save
                </GKButton>
              </Grid2>
            )}
          </Grid2>
        </form>

        <Show.When isTrue={openEditFeePlanModal}>
          <FeePlanDetailsModal
            open={openEditFeePlanModal}
            setOpen={setOpenEditFeePlanModal}
          />
        </Show.When>

        <Show.When isTrue={mapPortfolioModal}>
          <MapPortfolioModal
            open={mapPortfolioModal}
            setOpen={setMapPortfolioModal}
          />
        </Show.When>
      </FormProvider>
    </DashboardLayout>
  );
};

export default FeePlanEditForm;
